/**
 * The signed-in tenant's current booking (COL-533).
 *
 * Extracted from `Dashboard.tsx` so the V2 dashboard can read the same row
 * without importing the 9.5k-line V1 page. The predicate matches V1's in every
 * respect but one: the status list below also keeps the two move-out states,
 * for the reason given on that constant. That one difference is why this does
 * NOT share V1's cache entry — see the query key below.
 */

import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isContractSentForSignature } from "@/lib/tenantJourney";

/**
 * Statuses V1 treats as "this tenancy is the current one".
 *
 * Note this is broader than `ACTIVE_BOOKING_STATUSES` in
 * `hooks/v2/useProperties.ts`, which omits `pending`: that list answers "is
 * this room occupied", while this one answers "which booking is this tenant
 * looking at". A tenant whose booking is still `pending` very much needs to
 * see their contract and deposit, so `pending` stays in. Kept as its own
 * constant rather than reusing the occupancy list precisely because the two
 * answer different questions and must be free to diverge.
 */
const TENANT_VISIBLE_BOOKING_STATUSES = [
  "confirmed",
  "pending",
  "active",
  "approved",
  // Move-out states matter: without them this returns null for a tenant who
  // has given notice, V2 treats them as an applicant and the room page — with
  // the move-out protocol on it — disappears exactly when they need it. V1
  // survives its own narrow query only because it mounts Check-in/out
  // separately, via useActiveBooking, which lists these two.
  "notice_given",
  "move_out_pending",
] as const;

export const TENANT_BOOKING_QUERY_KEY = "active-booking";

/**
 * Keyed `["active-booking", uid, "tenant-v2"]` — a SUFFIX, deliberately.
 *
 * The key cannot be identical to V1's. React Query caches by key, so two
 * observers sharing one key share whichever queryFn mounted first, and this
 * one's status filter is wider than V1's (it keeps the two move-out states).
 * Under a shared key a tenant who has given notice would read whatever V1 had
 * already cached — null — and lose the room page with the move-out protocol
 * on it, which is the exact failure the wider list exists to prevent.
 *
 * Suffixing rather than renaming keeps the shared-invalidation half: React
 * Query matches prefixes, so V1's existing
 * `invalidateQueries(["active-booking", user.id])` still refreshes this one.
 * Both dashboards stay in sync without sharing a cache entry.
 */
export function useTenantBooking() {
  const { user } = useAuth();

  return useQuery<any | null>({
    queryKey: [TENANT_BOOKING_QUERY_KEY, user?.id, "tenant-v2"],
    enabled: !!user?.id,
    // V1 kept this fresh with a realtime channel on `bookings`, which is not
    // mounted while V2 is open. Without a poll a contract release or a key
    // pickup stays invisible — and in V2 those gate which tabs exist, so the
    // tenant stays locked out until the window regains focus. Same interval as
    // the payments ledger.
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await (supabase as any)
        .from("bookings" as any)
        .select(
          `
          *,
          rooms (
            id,
            title,
            floor,
            price,
            deposit_amount,
            images,
            properties (
              id,
              title,
              address,
              zip_code,
              city,
              image_url
            )
          )
        `
        )
        .eq("user_id", user.id)
        // An admin-archived booking is gone from the tenant's view even though
        // its status stays live — see the archive toggle in AdminTenants.
        .is("admin_archived_at", null)
        .in("status", TENANT_VISIBLE_BOOKING_STATUSES as unknown as string[])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return pickTenantBooking((data as any[]) ?? []);
    },
  });
}

/**
 * Which of several live bookings this dashboard is about.
 *
 * This used to be `.order(created_at desc).limit(1)` — the NEWEST row wins.
 * That is wrong the moment a tenant applies for a second room while already
 * living in one: the fresh application outranks the tenancy, so the dashboard
 * derived its journey stage, its money facts and its room page from a booking
 * with no contract, no deposit and no handover on it. The tenant's actual
 * tenancy simply disappeared from their own dashboard.
 *
 * A tenancy outranks an application, newest-first within each group. "Has
 * reached a contract" is `isContractSentForSignature`, the journey ladder's
 * own gate, so this cannot disagree with the stage derived from the row it
 * returns.
 *
 * Exported for the test; the hook is the only caller.
 */
export function pickTenantBooking(rows: readonly any[]): any | null {
  if (rows.length === 0) return null;
  const tenancy = rows.find((row) => isContractSentForSignature(row));
  // `rows` arrives newest-first from the query, so `find` already gives the
  // newest of each group — no second sort, and no re-deriving created_at
  // ordering in JS where a NULL would sort differently than it does in SQL.
  return tenancy ?? rows[0];
}
